<template>
  <v-hover>
    <template v-slot="{ hover }">
      <div
        class="nx-notification"
        :class="{ dark, read, pinned, hover, url }"
        @click="$emit('input')"
      >
        <section class="nx-notification-header">
          <nx-chip
            v-if="chip"
            :color="chipColor"
            text-color="white"
            class="mr-2"
            :value="translate(chip)"
          />
          <time
            :datetime="datetime"
            class="overflow-x-hidden text-clamp"
            v-text="humanDatetime"
          />
          <v-spacer />
          <v-slide-y-transition
            group
            class="d-flex"
            hide-on-leave
          >
            <v-btn
              v-if="pinned"
              key="pin"
              class="pa-0 ma-0"
              size="24"
              icon
              @click.stop.prevent="$emit('unpin')"
            >
              <v-icon
                size="24"
                :color="dark ? 'nx-neutral-200' : 'nx-neutral-600'"
              >
                push_pin
              </v-icon>
            </v-btn>
            <nx-notification-actions
              v-if="showActions"
              key="actions"
              :dark="dark"
              :hidden="!hover"
              :read="read"
              :pinned="pinned"
              v-on="$listeners"
            />
          </v-slide-y-transition>
          <v-btn
            v-if="closable"
            class="pa-0 ma-0"
            size="24"
            icon
            @click.stop.prevent="dismiss"
          >
            <v-icon
              size="20"
              :color="dark ? 'nx-neutral-200' : 'nx-neutral-600'"
            >
              close
            </v-icon>
          </v-btn>
        </section>
        <section class="nx-notification-content">
          <h3>{{ translatedTitle }}</h3>
          <v-img
            v-if="image"
            width="56"
            max-width="56"
            height="56"
            max-height="56"
            :src="image"
          />
        </section>
      </div>
    </template>
  </v-hover>
</template>

<script>
import NxChip from '@/components/chips/Chip.vue';
import moment from 'moment';
import capitalize from 'lodash/capitalize';
import NxNotificationActions from '@/components/notification/NotificationActions.vue';
import { translateCandidates } from '@/utils';
import { uncapitalize } from '@/filters';

export default {
  name: 'NxNotification',
  components: {
    NxNotificationActions,
    NxChip,
  },
  props: {
    chip: {
      type: String,
      default: null,
    },
    chipColor: {
      type: String,
      default: 'nx-accent-500',
    },
    datetime: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    titleVariables: {
      type: Object,
      default: () => ({}),
    },
    image: {
      type: String,
      default: null,
    },
    url: {
      type: String,
      default: null,
    },
    autoClose: {
      type: Number,
      default: null,
    },
    closable: {
      type: Boolean,
    },
    dark: {
      type: Boolean,
    },
    read: {
      type: Boolean,
    },
    pinned: {
      type: Boolean,
    },
    showActions: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    translatedTitleVariables() {
      const result = {};
      Object.keys(this.titleVariables).forEach((key) => {
        result[key] = uncapitalize(this.translate(this.titleVariables[key]));
      });
      return result;
    },
    translatedTitle() {
      return this.translate(this.title, this.translatedTitleVariables);
    },
    humanDatetime() {
      const now = moment();
      const datetime = moment(this.datetime);
      if (now.diff(datetime, 'days') > 1) {
        return datetime.format('DD/MM/YYYY - HH:mm');
      }
      return capitalize(datetime.fromNow());
    },
  },
  mounted() {
    if (this.autoClose) {
      this.timeout = setTimeout(this.autoclose, this.autoClose);
    }
  },
  beforeDestroy() {
    clearTimeout(this.timeout);
  },
  methods: {
    translate(translatable, variables = []) {
      return translateCandidates(
        [
          `message.${translatable}`,
          translatable,
        ],
        translatable,
        variables,
      );
    },
    dismiss() {
      this.$emit('dismiss');
    },
    autoclose() {
      this.$emit('autoclose');
    },
  },
};
</script>

<style lang="stylus" scoped>
@import '../../assets/stylus/variables.styl'

.nx-notification {
  display: flex;
  flex-direction: column;
  gap: 12px;
  will-change: background-color;
  transition: background-color .2s;
  background-color: white;
  width: 100%
  min-height: 100px;
  box-shadow: $nx-elevation-700;
  padding: 12px 12px 16px 12px;
  border-radius: 8px;
  cursor: default;

  .dark& {
    background: $nx-neutral-800;
    border-radius: 0;
    box-shadow: none;
    .hover& {
      background: $nx-neutral-700;
      opacity: 1;
    }
  }

  .read& {
    opacity: 0.6;
  }

  .pinned& {
    opacity: 1;
  }

  .url& {
    cursor: pointer;
  }

  &-header {
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 24px;
    time {
      font-size: 12px;
      font-style: normal;
      font-weight: 500;
      color: $nx-neutral-600;
      .dark & {
        color: $nx-neutral-500;
      }
    }
  }

  &-content {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    gap: 16px;

    h3 {
      font-size: 14px;
      font-style: normal;
      font-weight: 600;
      color: $nx-neutral-900;
      .dark & {
        color: white;
      }
      .read & {
        font-weight: 500;
      }
    }
  }
}
</style>
